<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3">
      <SimplePageHeader title="Users" class="px-4 pr-5 mb-5">
        <template v-slot:right-buttons>
          <ButtonCustomIcon
            icon="add"
            color="primary"
            text="Add"
            :routeName="userCreateRoute.name"
            :block="true"
          />
        </template>
      </SimplePageHeader>
      <UserFilters
        class="px-5 mb-sm-5"
        :selectedUserId="selectedUserId"
        @selectedUserIdChange="handleSelectedUserIdChange"
        @search="handleSearchClick"
        @clear="handleSearchClick"
      ></UserFilters>
    </div>
    <v-container fluid class="px-0 pt-2 mt-4">
      <v-layout column>
        <v-data-table
          dense
          :headers="headers"
          :items="userList"
          :options.sync="options"
          :server-items-length="serverItemsLength"
          class="elevation-2"
        >
          <template v-slot:item.active="{ item }">
            <v-simple-checkbox
              v-model="item.active"
              disabled
            ></v-simple-checkbox>
          </template>
          <template v-slot:item.securityRoles="{ item }">
            {{ item.securityRoles | roleLabel }}
          </template>
          <template v-slot:item.action="{ item }">
            <v-btn
              class="mx-1 my-1"
              color="primary"
              outlined
              @click.prevent="editUser($event, item)"
            >
              <v-icon color="primary">edit</v-icon>
            </v-btn>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn class="mx-0 my-1"
                       color="primary"
                       outlined
                       v-bind="attrs"
                       v-on="on"
                       @click="editUserPassword(item)"
                >
                  <v-icon color="primary">vpn_key</v-icon>
                </v-btn>
              </template>
              <span>Change User's Password</span>
            </v-tooltip>
          </template>
        </v-data-table>
        <UserPasswordChangeModal
          :userID="currentUserID"
          ref="userPasswordModal"
        ></UserPasswordChangeModal>
      </v-layout>
    </v-container>
    <BottomSpacer></BottomSpacer>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import UserFilters from './UserFilters'
import { userCreateRoute, userEditRoute } from '@/lib/constants/routes'
import { BottomSpacer, ButtonCustomIcon } from '@/components/buttons'
import { SimplePageHeader } from '@/components/pageComponents'
import UserPasswordChangeModal from '@/components/users/UserPasswordChangeModal'
import { parseGridOptionsFromQuery, buildPaginationQueryParams, isAllRecordsSortOnlyChange, setOrDeleteQueryParam, hasQueryChanged } from '@/lib/pagingAndSorting'

export default {
  components: {
    UserPasswordChangeModal,
    UserFilters,
    BottomSpacer,
    ButtonCustomIcon,
    SimplePageHeader
  },
  data () {
    return {
      userList: [],
      userCreateRoute,
      headers: [
        { text: 'First Name', value: 'firstName', width: '13.6%' },
        { text: 'Last Name', value: 'lastName', width: '13.6%' },
        { text: 'Email', value: 'emailAddress', width: '26.6%' },
        { text: 'Security Roles', value: 'securityRoles', align: 'center', width: '16.6%' },
        { text: 'Active', value: 'active', align: 'center', width: '8%', sortable: false },
        { text: 'Actions', value: 'action', align: 'center', class: 'text-xs-right', sortable: false, width: '22.6%' }
      ],
      showModal: false,
      modalTitle: '',
      currentUserID: 0,
      selectedUserId: null,
      hasFetchedAllUsers: false,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['firstName'],
        sortDesc: [false]
      }
    }
  },
  computed: {
    ...mapFields('users', [
      'totalRecords'
    ]),
    ...mapFields('userFilters', [
      'firstName',
      'lastName',
      'securityRoles',
      'active'
    ]),
    serverItemsLength () {
      return this.options.itemsPerPage === -1 ? undefined : this.totalRecords
    }
  },
  watch: {
    options: {
      handler (newOptions, previousOptions) {
        this.syncOptionsToQuery()

        if (!this.shouldFetchUsers(newOptions, previousOptions)) {
          return
        }

        this.fetchUsers()
      },
      deep: true
    }
  },
  created () {
    this.applyFiltersFromQuery()
    this.options = {
      ...this.options,
      ...this.getOptionsFromQuery()
    }
    this.syncOptionsToQuery()
  },
  methods: {
    ...mapActions('users', [
      'getUsersWithFiltersPaginated',
      'getUsersWithFilters'
    ]),
    editUser (event, user) {
      if (event.ctrlKey === true) {
        let route = this.$router.resolve({ name: userEditRoute.name, params: { id: user.id } })
        window.open(route.href, '_blank')
      } else {
        this.$router.push({ name: userEditRoute.name, params: { id: user.id } })
      }
    },
    editUserPassword (user) {
      this.currentUserID = user.id
      this.$refs.userPasswordModal.showModal()
    },
    getOptionsFromQuery () {
      return parseGridOptionsFromQuery(this.$route.query, this.options)
    },
    applyFiltersFromQuery () {
      const { firstName, lastName, securityRoles, active, userId } = this.$route.query

      this.firstName = typeof firstName === 'string' && firstName.length ? firstName : null
      this.lastName = typeof lastName === 'string' && lastName.length ? lastName : null

      if (Array.isArray(securityRoles)) {
        this.securityRoles = securityRoles.length ? securityRoles[0] : null
      } else if (typeof securityRoles === 'string' && securityRoles.length) {
        this.securityRoles = securityRoles.includes(',')
          ? securityRoles.split(',').filter(role => role)[0] || null
          : securityRoles
      } else {
        this.securityRoles = null
      }

      if (active === 'true') {
        this.active = true
      } else if (active === 'false') {
        this.active = false
      } else if (active === 'null') {
        this.active = null
      } else {
        this.active = true
      }

      const parsedUserId = Number(userId)
      this.selectedUserId = Number.isInteger(parsedUserId) && parsedUserId > 0 ? parsedUserId : null
    },
    syncOptionsToQuery () {
      const { sortColumn, sortDirection, ...paginationParams } = buildPaginationQueryParams(this.options)

      const nextQuery = { ...this.$route.query, ...paginationParams }

      setOrDeleteQueryParam(nextQuery, 'sortColumn', sortColumn)
      setOrDeleteQueryParam(nextQuery, 'sortDirection', sortColumn ? sortDirection : null)

      setOrDeleteQueryParam(nextQuery, 'firstName', this.firstName)
      setOrDeleteQueryParam(nextQuery, 'lastName', this.lastName)
      if (this.active === null) {
        nextQuery.active = 'null'
      } else {
        setOrDeleteQueryParam(nextQuery, 'active', this.active)
      }
      setOrDeleteQueryParam(nextQuery, 'userId', this.selectedUserId)

      const rolesValue = Array.isArray(this.securityRoles) && this.securityRoles.length
        ? this.securityRoles.join(',')
        : (typeof this.securityRoles === 'string' && this.securityRoles.length ? this.securityRoles : null)
      setOrDeleteQueryParam(nextQuery, 'securityRoles', rolesValue)

      if (hasQueryChanged(this.$route.query, nextQuery, ['pageNumber', 'pageSize', 'sortColumn', 'sortDirection', 'firstName', 'lastName', 'securityRoles', 'active', 'userId'])) {
        this.$router.replace({ query: nextQuery })
      }
    },
    fetchUsers () {
      const { page, itemsPerPage, sortBy, sortDesc } = this.options

      // itemsPerPage = -1 means fetch all users
      if (itemsPerPage === -1) {
        this.getUsersWithFilters().then(users => {
          this.hasFetchedAllUsers = true
          this.userList = users
        })
      } else {
        this.hasFetchedAllUsers = false
        this.getUsersWithFiltersPaginated({
          pageNumber: page,
          pageSize: itemsPerPage,
          sortColumn: sortBy && sortBy.length ? sortBy[0] : null,
          sortDirection: sortDesc && sortDesc.length ? (sortDesc[0] ? 'DESC' : 'ASC') : null
        }).then(users => {
          this.userList = users
        })
      }
    },
    shouldFetchUsers (newOptions, previousOptions) {
      return !isAllRecordsSortOnlyChange(newOptions, previousOptions, this.hasFetchedAllUsers)
    },
    handleSearchClick () {
      this.options = { ...this.options, page: 1 }
    },
    handleSelectedUserIdChange (userId) {
      this.selectedUserId = userId
    },
    closeDialog () {
      this.showModal = false
    }
  }
}
</script>
